import { formatDate as ngFormatDate, formatNumber as ngFormatNumber } from '@angular/common';
import { digger } from '@cbre/digger';
import moment from 'moment';
import { UserSettings } from '../user-settings/user-settings.class';
import { defaultTxt } from './strings.constants';
import { CE_DOT_COMMA_LOCALES } from './ce-locales.const';
import { getCurrentLocale } from './locale-utils';

const MASKING_TOKEN = '*****';

export interface MiqSanitizers {
  Number: Function;
  Boolean: Function;
  Date: Function;
  formatNumber: Function;
  isMasked: Function;
  MiqMoment: any;
}

/**
 * Returns whether value is equal to the masked wildcard
 */
const isMasked = x => x === MASKING_TOKEN;

/**
 * Attempts conversion only if value is not the masked value
 * @param x: any value, intention is to receive numeric values
 *
 * @returns Number value OR mask wildcard value
 */
const MiqNumber = x => (isMasked(x) ? x : Number(x));

/**
 * Wrapper function for @angular/common/formatNumber but before using angular we ask return masked
 * value so we don't fall in errors trying to convert wildcard '*****'into a number
 *
 * !!!!! `locale` IS ACCEPTED AND THEN DELIBERATELY IGNORED. DO NOT "FIX" THIS. !!!!!
 *
 * The value is ALWAYS formatted for the current user locale (`getCurrentLocale()`). 18 call sites in
 * the auto-calculation write-back path call this as `formatNumber(v, 'en', fmt)` and then re-parse
 * the result with `parseFormattedNumber`, which branches on `getCurrentLocale()`
 * (`src/app/records/controls/common/listeners/common/common-listener-helper.ts`). Honouring `'en'`
 * would hand a Polish user's `1,234.56` to `parseCECommaDotFormattedValue`, which strips dots and
 * finds no comma, producing `123456` — a 100x corruption persisted to EDP. The sites are
 * `auto-calc-round.ts` (1), `sale-auto-calc-listeners.ts` (9), `common-listener-helper.ts` (4) and
 * `business-rules/common/common-rate.ts` (4).
 *
 * The parameter is kept only so those call sites still compile. New code must use `formatMiqNumber`
 * (`./locale-number.ts`), which has no positional locale at all and offers `opts.localeOverride` for
 * the genuinely locale-invariant cases.
 *
 * @param val: numeric value to format OR '*****' wildcard
 * @param locale IGNORED — see above. Present for backwards compatibility only.
 * @param digits Decimal representation options, specified by a string in the following format:
 * @param defaultText Default text value to return if val us undefined or null
 *
 * @returns The formatted text string.'en-US', '.0-2'
 * @see [Internationalization (i18n) Guide](https://angular.io/guide/i18n)
 */
function MiqFormatNumber(val, locale?: string, digits = '.0-2', defaultText = defaultTxt): string {
  let result: string;
  const effectiveLocale = getCurrentLocale();
  const isParsable = val != null && !Number.isNaN(Number(val));
  if (val != null && (isMasked(val) || !isParsable)) {
    result = val as string;
  } else {
    result = val == null ? defaultText : ngFormatNumber(Number(val), effectiveLocale, digits);
    // Fix Austria and other CE locales that use dot as thousands separator
    // Angular's locale data uses space, but these locales require dot
    if (effectiveLocale && CE_DOT_COMMA_LOCALES.has(effectiveLocale)) {
      result = result.replace(/\s/g, '.');
    }
  }
  return result;
}

/**
 * L2-S6: the HOST's `MiqFormatNumber` semantics, available in this library under a name that says
 * what it does — it HONOURS its `locale` argument.
 *
 * This is the compatibility half of the `@shared/utils/helpers/sanitizers` divergence documented on
 * {@link MiqFormatNumber} above: the host copy resolves `locale || getCurrentLocale()`, this
 * library's `formatNumber` ignores `locale` entirely. Both behaviours are needed and NEITHER may be
 * changed:
 *
 *   - `formatNumber` (MiqFormatNumber) is what the 18 auto-calculation write-back sites depend on.
 *     They call it with `'en'` and re-parse the result against the USER's locale, so honouring the
 *     argument there would corrupt saved EDP values by 100x. Read its comment before touching it.
 *   - `formatNumberForLocale` is what a caller that is repointed OFF the host file needs, so its
 *     output stays byte-identical to what the host produced.
 *
 * The body is a line-for-line port of `src/app/shared/utils/helpers/sanitizers.ts`'s
 * `MiqFormatNumber`, with one intentional addition — the `defaultText` parameter this library's
 * `formatNumber` already has (the host hardcodes `'-'`, and `defaultTxt` IS `'-'`, so the default
 * argument reproduces the host exactly).
 *
 * The one REMAINING difference from the host, unavoidable and pre-existing for every member of this
 * file: `isMasked` here compares against a hardcoded `'*****'`, whereas the host reads
 * `environment.appConstants.MASKING_TOKEN` through `constantTimeEquals(String(x), …)`. Those agree
 * for every environment in the repo.
 *
 * @param val numeric value to format, or the `'*****'` mask wildcard
 * @param locale Angular locale code; falsy falls back to `getCurrentLocale()`
 * @param digits Angular `DecimalPipe` digitsInfo string
 * @param defaultText returned when `val` is null/undefined
 */
function formatNumberForLocale(val, locale?: string, digits = '.0-2', defaultText = defaultTxt): string {
  let result: string;
  const effectiveLocale = locale || getCurrentLocale();
  const isParsable = val != null && !Number.isNaN(Number(val));
  if (val != null && (isMasked(val) || !isParsable)) {
    result = val as string;
  } else {
    result = val == null ? defaultText : ngFormatNumber(Number(val), effectiveLocale, digits);
    // Fix Austria and other CE locales that use dot as thousands separator
    // Angular's locale data uses space, but these locales require dot
    if (effectiveLocale && CE_DOT_COMMA_LOCALES.has(effectiveLocale)) {
      result = result.replace(/\s/g, '.');
    }
  }
  return result;
}

/**
 * Wrapper function for @angular/common/i18/formatDate but before using angular we ask return masked
 * value so we don't fall in errors trying to convert wildcard '*****'into a date
 * @param val
 * @param format
 * @param locale
 * @param timezone
 */
const MiqFormatDate = (val: string | number | Date, format: string, locale: string, timezone: string = undefined) =>
  isMasked(val) ? val : ngFormatDate(val, format, locale, timezone);

/**
 * Attempts conversion only if value is not the masked value
 * @param x: Boolean value, intention is to receive Boolean values
 *
 * @returns Boolean object OR mask wildcard value
 */
const MiqBool = x => (isMasked(x) ? x : Boolean(x));

/**
 * It returns masked if masked, otherwise converts to boolean and returns formatted text for each value
 * @example
 * boolToString(false, 'foo', 'bar'); // 'bar'
 * boolToString(true); // 'Yes'
 * boolToString('*****'); // '*****'
 *
 * @param value Intended value to be converted to boolean
 * @param yesText Text to be used for truthy values (default 'Yes')
 * @param noText Text to be used for falsy values (default 'No)
 * @param emptyText Text to be used for empty value
 *
 * @returns string
 */
const boolToString = (value: any, yesText = 'Y', noText = 'N', emptyText = '') => {
  return isMasked(value) || ['', null].includes(value) ? emptyText : Boolean(value) ? yesText : noText;
};

/**
 * Receives a date string and returns a formatted value
 * @param d, a date string
 * @param fmt (OPTIONAL), format to be returned, DEFAULTED TO 'MM-DD-YYYY'
 *
 * @returns formatted date
 */
const momentFormat = (d, fmt = 'M/D/YYYY') => (isMasked(d) ? d : moment(d).utc().format(fmt));

/**
 * It uses an specific input format and produces a different date format
 * @param d: Date value or wildcard
 * @param inFmt: specific date format, it specifies how to read the date
 * @param outFmt: the output date format to apply
 * @param toUtc Flag to convert date to UTC
 *
 * @returns formatted date string
 */
const momentInputFormat = (d, inFmt, outFmt = UserSettings.getGlobalDateDisplayFormat('value'), toUtc = true) =>
  isMasked(d) ? d : toUtc ? moment(d, inFmt).utc().format(outFmt) : moment(d).format(outFmt);

/**
 * Returns a built in formatted string from moment.fromNow function e.g '3 minutes ago'
 * @param t, a date string
 *
 * @returns fromNow moment value
 */
const fromNow = t => (isMasked(t) ? t : moment(t).fromNow());

const UTC_INPUT_FORMAT = `${moment.HTML5_FMT.DATETIME_LOCAL}Z`; // 'YYYY-MM-DDTHH:mmZ'

/**
 * L2-R8 (ADDITIVE ONLY — nothing in this library or either app is repointed at these yet).
 *
 * Ports `MiqMoment.getFormattedDate` / `MiqMoment.extractAndFormatDate` from the host copy at
 * `src/app/shared/utils/helpers/sanitizers.ts` so this object is a strict superset of it. The
 * reports remote calls them 30 times (24 + 6) and currently reaches into `@shared/utils/helpers/
 * sanitizers` to do so; that seam cannot be closed yet because the SAME file's `MiqFormatNumber`
 * diverges between host and lib over its `locale` argument (CM-28992 / CM-31519 patched the two
 * copies differently) and that is an open product decision.
 *
 * Two things had to be made EXPLICIT to keep these faithful to the host, both of them defaults that
 * differ between the two copies of this file:
 *
 *  1. `momentInputFormat`'s third argument. The host default is the literal `'MM/DD/YYYY'`; the lib
 *     default is `UserSettings.getGlobalDateDisplayFormat('value')`, i.e. the USER's format. Left
 *     implicit, a `DD/MM/YYYY` user would make moment emit '31/12/2022', which Angular's formatDate
 *     then cannot parse. So `'MM/DD/YYYY'` is passed explicitly below.
 *
 *  2. The output format. The host resolves it with `getGlobalDateDisplayFormat()` from
 *     @miq/platform, which this library CANNOT import (@miq/platform imports @miq/utils — the edge
 *     only runs one way). The nearest lib-native equivalent is
 *     `UserSettings.getGlobalDateDisplayFormat('code')`, and it is identical WHENEVER THE USER HAS A
 *     SAVED globalDateFormat PREFERENCE (both read the same `USER_SETTINGS` localStorage entry and
 *     return `globalDateFormat[0].code`). !! KNOWN DIVERGENCE !! With NO saved preference the
 *     platform version falls back per locale — `'M/d/yyyy'` for US, `'dd/MM/yyyy'` otherwise (via
 *     `isUSLocale()`) — while `UserSettings` falls back to `'M/d/yyyy'` unconditionally. So a non-US
 *     user who has never saved a date preference would see US-ordered dates from the lib copy.
 *     Rather than reimplement `isUSLocale()` here (it would read a DIFFERENT storage key —
 *     `localeId` via getLocaleIdFromStorage() — rather than the SSO user-info blob, so it would be a
 *     third semantics, not a match), the format is exposed as an explicit trailing parameter:
 *     whoever repoints the 30 reports call sites can pass @miq/platform's
 *     `getGlobalDateDisplayFormat()` straight in and get byte-identical output. Do not repoint them
 *     without doing that, or without resolving the fallback difference first.
 *
 * Also note `formatDate` here is Angular's (`ngFormatDate`), matching the host — NOT the local
 * masked-aware `MiqFormatDate`. `isMasked` already short-circuits above it, so the two agree, but
 * the host's `isMasked` reads `environment.appConstants.MASKING_TOKEN` and coerces with
 * `String(...)` via `constantTimeEquals`, whereas this file's `isMasked` is `x === '*****'` on a
 * hardcoded token. That pre-existing difference applies to EVERY member of this object, not just the
 * two added here.
 *
 * NAME NOTE: `lib/data-retrievers/common.ts` already exports a free function called
 * `getFormattedDate` (plus `getAndFormatDate`) on this library's public barrel. Those are DEAD CODE
 * (zero importers repo-wide) and are NOT equivalent — they hardcode `'YYYY-MM-DD'` in and
 * `'M/d/yyyy'` out, ignoring the user's preference entirely. The additions below are properties of
 * `MiqMoment`, so they do not collide with those barrel exports; they also must not be confused with
 * them.
 */
function getFormattedDate(
  date: string,
  displayFormat: string = UserSettings.getGlobalDateDisplayFormat('code')
): string {
  return isMasked(date)
    ? date
    : date && ngFormatDate(momentInputFormat(date, UTC_INPUT_FORMAT, 'MM/DD/YYYY'), displayFormat, 'en-US');
}

const extractAndFormatDate = (record, path, defaultText = defaultTxt, displayFormat?: string): any => {
  try {
    const date = digger(record, path);
    // `displayFormat` is forwarded as-is: when it is undefined, getFormattedDate's own default
    // (UserSettings.getGlobalDateDisplayFormat('code')) applies, exactly once per call.
    return date != null && !!date.trim() ? getFormattedDate(date, displayFormat) : defaultText;
  } catch (e) {
    console.error(e);
    return defaultText;
  }
};

const MiqMoment = {
  fromNow,
  getFormattedDate,
  extractAndFormatDate,
  format: momentFormat,
  formatFromInputFormat: momentInputFormat,
  utcInputFormat: UTC_INPUT_FORMAT, // this is the resulting value => 'YYYY-MM-DDTHH:mmZ'
};

export {
  MiqNumber,
  MiqBool,
  boolToString,
  MiqFormatNumber as formatNumber,
  formatNumberForLocale,
  MiqFormatDate as formatDate,
  isMasked,
  MiqMoment,
};
